/* AgentScriptCompiler
   Compiles inbound.md and outbound.md script books into a single system_prompt string
   suitable for ElevenLabs Agents. Uses Vite raw imports to bundle the MD sources.
*/
import inboundSource from '../../../inbound.md?raw';
import outboundSource from '../../../outbound.md?raw';

export type ScriptMode = 'inbound' | 'outbound';

export interface CompileOptions {
  mode: ScriptMode;
  companyName?: string;
}

/**
 * Extract a section of a markdown document between a start label and the next SECTION header.
 */
function extractSection(md: string, sectionTitle: string): string {
  const startIdx = md.indexOf(sectionTitle);
  if (startIdx === -1) return '';
  const rest = md.slice(startIdx);
  // Next SECTION header (e.g., "SECTION 2:" ...)
  const nextMatch = rest.match(/\nSECTION\s+\d+:/);
  if (!nextMatch) return rest.trim();
  const endIdx = nextMatch.index || rest.length;
  return rest.slice(0, endIdx).trim();
}

/**
 * Simple utility to extract bullet lists under a heading label until a blank line or another heading.
 */
function extractBulletsAfter(label: string, source: string): string[] {
  const idx = source.indexOf(label);
  if (idx === -1) return [];
  const sub = source.slice(idx + label.length);
  const lines = sub.split('\n');
  const bullets: string[] = [];
  for (const line of lines) {
    const t = line.trim();
    if (!t) break;
    if (/^[-*•]/.test(t) || /^✅|^❌/.test(t)) {
      bullets.push(t.replace(/^[-*•]\s*/, '• ').trim());
    } else if (/^[A-Z].*:/.test(t)) {
      // stop on a new heading-ish line
      break;
    } else {
      // accumulate until a blank/heading
      bullets.push(t);
    }
  }
  return bullets;
}

function sanitizeForPrompt(text: string): string {
  // Trim and compress trailing spaces; preserve newlines
  return text.replace(/[ \t]+$/gm, '').trim();
}

export function getScriptBook(mode: ScriptMode): string {
  return mode === 'inbound' ? inboundSource : outboundSource;
}

/**
 * Build explicit tool usage guidance for Successware toolchain with variable handoffs and escalation rules.
 * The agent must follow this sequence to maximize booking probability.
 */
function buildToolingGuidance(mode: ScriptMode): string {
  const sequence = [
    '1) search_customers: search by phoneNumber and/or name.',
    '   - If a match is found: use that customerId.',
    '   - If no match: proceed to create_customer.',
    '2) create_customer: create a new customer record with collected profile details.',
    '   - Output: customerId (persist and reuse).',
    '3) create_job: create a job for the customer with jobType and description.',
    '   - Output: jobId (persist and reuse).',
    '4) calculate_capacity: fetch available slots for the desired date and serviceAddress (or verified address).',
    '   - Use 4-hour service windows unless the book specifies firm times.',
    '5) schedule_job: schedule using jobId, date, and timeWindow agreed with the caller (Two-Option Close).',
  ].join('\n');

  const handoffs = [
    'Variable handoffs:',
    '- customerId: from search_customers match or create_customer result.',
    '- jobId: from create_job.',
    '- date/timeWindow: confirmed with the customer using the Two-Option Close.',
  ].join('\n');

  const policy = [
    'Tool input policy:',
    '- Validate and confirm all inputs aloud. Never fabricate data.',
    '- Never quote repair prices or ballparks when forbidden; explain policy and book the appropriate visit.',
  ].join('\n');

  const escalationCommon = [
    'Escalation rules:',
    '- If progress stalls after 2–3 objection cycles or ~90 seconds without forward movement, invoke transfer_to_human.',
    '- Provide: { callSid, reason (concise), summary (1–2 sentences), urgency ("normal"|"high"|"emergency") }.'
  ].join('\n');

  const inboundAdd = '- For emergencies or safety issues, escalate immediately with urgency="emergency".';
  const outboundAdd = '- For outbound calls, begin with identity + purpose + brief value, and follow any required disclaimers from the script book.';

  return [
    sequence,
    '',
    handoffs,
    '',
    policy,
    '',
    escalationCommon,
    mode === 'inbound' ? inboundAdd : outboundAdd,
  ].join('\n');
}

/**
 * Compile inbound/outbound script book into a system prompt.
 * Strategy:
 * - Persona + guardrails
 * - Embed "Mandatory Call Flow & Rules" verbatim as Hard Rules
 * - Pull pricing allowed/forbidden bullets when present
 * - Include booking window rules (if present)
 * - Include Two-Option Close and Persistence summary (if present)
 * - Reference that the full script book is provided as contextual knowledge (embedded after the prompt)
 */
export function compileSystemPrompt(options: CompileOptions): { systemPrompt: string; knowledgeAppendix: string } {
  const { mode, companyName = 'Right Away Home Services' } = options;
  const md = getScriptBook(mode);

  // Core sections from the books
  const mandatorySection = extractSection(md, 'SECTION 1: MANDATORY CALL FLOW & RULES');
  const pricingAllowed = extractBulletsAfter('PRICING YOU CAN DISCUSS', mandatorySection);
  const pricingForbidden = extractBulletsAfter('PRICING YOU CANNOT DISCUSS', mandatorySection);
  const bookingRules = extractBulletsAfter('BOOKING RULES', mandatorySection);

  // Capture a brief persistence blurb (found in inbound Section 1 and later detailed Section 12)
  const persistenceSnippet =
    extractBulletsAfter('Persistence', mandatorySection).join('\n') ||
    extractSection(md, 'SECTION 12: PERSISTENCE TECHNIQUES');

  // Two-Option Close mention
  const twoOptionCloseLine =
    (mandatorySection.match(/Two-Option Close[\s\S]*?\n/) || [])[0] ||
    'Never ask “Do you want to schedule?”; ask “Would morning or afternoon work better?”';

  // Build prompt
  const persona = `You are the live voice AI agent for ${companyName}. You handle ${mode === 'inbound' ? 'inbound calls (repairs, maintenance, duct cleaning, plumbing, appliance, and sales inquiries)' : 'outbound calls (follow-ups, sales, scheduling, and service calls)'} with a professional, warm, and confident tone.`;

  let guardrails = [
    'Follow the Mandatory Call Flow exactly in order.',
    'Obey pricing rules. Only discuss pricing explicitly allowed; never quote repair costs or ballparks where forbidden.',
    'Use the Two-Option Close and persistence techniques professionally (2–3 attempts maximum).',
    'Use four-hour service windows unless the script specifies firm times.',
    'Never promise technician arrival outside policy.',
    'If caller requests a human or situation requires escalation, invoke the transfer_to_human tool with callSid, reason, summary, and urgency.',
  ];

  if (mode === 'outbound') {
    guardrails.push('Include required outbound disclaimers from the script book before proceeding.');
  }

  const hardRules = sanitizeForPrompt(mandatorySection || '');
  const pricingBlock =
    (pricingAllowed.length || pricingForbidden.length)
      ? `PRICING YOU CAN DISCUSS:\n${pricingAllowed.map(b => `- ${b}`).join('\n')}\n\nPRICING YOU CANNOT DISCUSS:\n${pricingForbidden.map(b => `- ${b}`).join('\n')}`
      : '';

  const bookingBlock =
    bookingRules.length ? `BOOKING WINDOWS AND SCHEDULING RULES:\n${bookingRules.map(b => `- ${b}`).join('\n')}` : '';

  const persistenceBlock = sanitizeForPrompt(
    typeof persistenceSnippet === 'string' && persistenceSnippet.length < 2000
      ? persistenceSnippet
      : 'Use polite, professional persistence to overcome objections 2–3 times; always provide new value or a reframe, then accept the decision.'
  );

  const systemPrompt = sanitizeForPrompt(
`SYSTEM ROLE
${persona}

CORE GUARDRAILS
${guardrails.map((g) => `- ${g}`).join('\n')}

HARD RULES (Embed; obey verbatim)
${hardRules}

${pricingBlock ? `\n${pricingBlock}\n` : ''}

${bookingBlock ? `\n${bookingBlock}\n` : ''}

TWO-OPTION CLOSE
${twoOptionCloseLine.trim()}

PERSISTENCE
${persistenceBlock}

TOOLING AND ESCALATION
- Use registered tools when applicable (e.g., Successware tools for search/schedule; transfer_to_human to escalate live calls). Provide concise reason, conversation summary, and urgency when escalating.
- When in doubt about quoting or scope, transparently explain policy and schedule the appropriate visit.

TOOL USAGE GUIDANCE
${buildToolingGuidance(mode)}

OUTPUT STYLE
- Speak naturally and concisely.
- Confirm details you collect.
- Keep the flow moving toward booking.
- End with next steps and a polite close.`
  );

  // Append the full script book as knowledge
  const knowledgeAppendix = sanitizeForPrompt(md);

  return { systemPrompt, knowledgeAppendix };
}
